import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Clickable templates for everything that is not a labeled action button.
 * See docs/ux/BUTTON-SYSTEM.md.
 *
 * - `Pressable`        base: any custom clickable (keeps your layout classes)
 * - `RowButton`        list rows, menu-like items, clickable cards
 * - `ChipButton`       filter / choice chips (pill, optional selected state)
 * - `InlineIconButton` small icon inside a chip or a line of text (remove ×, edit ✎)
 *
 * All share the same focus ring, disabled state and transition as `Button`.
 * For labeled actions use `Button`; for 32×32 toolbar icons use `IconButton`.
 */
export const pressableBase = [
  "cursor-pointer outline-none transition-[background-color,color,border-color,opacity] duration-150 ease-out",
  "focus-visible:ring-[3px] focus-visible:ring-ring/50",
  "disabled:pointer-events-none disabled:opacity-50",
].join(" ")

export type PressableProps = React.ButtonHTMLAttributes<HTMLButtonElement>

/** Unstyled clickable with the shared focus / disabled behavior. */
export const Pressable = React.forwardRef<HTMLButtonElement, PressableProps>(
  function Pressable({ className, type = "button", ...props }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(pressableBase, className)}
        {...props}
      />
    )
  }
)

export const rowButtonVariants = cva(pressableBase, {
  variants: {
    /**
     * `row`: the standard row — full width, icon / text / trailing on one
     * centered line, rounded, with hover and selected states (new code).
     * `none`: adds nothing visual; the element keeps its own classes
     * (migrated rows, stacked content, custom grids).
     */
    layout: {
      row: "flex w-full min-w-0 items-center gap-2 rounded-lg text-left text-sm",
      none: "",
    },
    /** Padding preset for `row`; `none` when the row sets its own spacing. */
    density: {
      none: "",
      compact: "px-2 py-1.5",
      default: "px-3 py-2",
    },
    selected: { true: "", false: "" },
  },
  compoundVariants: [
    {
      layout: "row",
      selected: true,
      className: "bg-accent text-accent-foreground",
    },
    { layout: "row", selected: false, className: "hover:bg-muted/50" },
  ],
  defaultVariants: { layout: "row", density: "default", selected: false },
})

export type RowButtonProps = PressableProps &
  VariantProps<typeof rowButtonVariants>

/** Full-width clickable row: lists, pickers, menu-like items, cards. */
export const RowButton = React.forwardRef<HTMLButtonElement, RowButtonProps>(
  function RowButton(
    { className, layout, density, selected, type = "button", ...props },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type}
        data-selected={selected ? "true" : undefined}
        className={cn(
          rowButtonVariants({ layout, density, selected }),
          className
        )}
        {...props}
      />
    )
  }
)

export const chipButtonVariants = cva(
  [
    pressableBase,
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-medium",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "h-6 px-2 text-2xs [&_svg]:size-3",
        md: "h-7 px-3 text-xs [&_svg]:size-3.5",
      },
      selected: {
        true: "border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
        false: "border-border bg-background text-foreground hover:bg-accent",
      },
    },
    defaultVariants: { size: "md", selected: false },
  }
)

export type ChipButtonProps = PressableProps &
  VariantProps<typeof chipButtonVariants>

/** Pill for filters and choices; `selected` fills it with the accent. */
export const ChipButton = React.forwardRef<HTMLButtonElement, ChipButtonProps>(
  function ChipButton(
    { className, size, selected, type = "button", ...props },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type}
        aria-pressed={selected ?? undefined}
        className={cn(chipButtonVariants({ size, selected }), className)}
        {...props}
      />
    )
  }
)

export const inlineIconButtonVariants = cva(
  [
    pressableBase,
    "inline-flex shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground",
  ].join(" "),
  {
    variants: {
      size: {
        /** Inside a chip or tag (remove ×). */
        xs: "size-4 [&_svg]:size-3",
        /** Next to text (edit ✎, copy). */
        sm: "size-6 [&_svg]:size-3.5",
      },
    },
    defaultVariants: { size: "sm" },
  }
)

export type InlineIconButtonProps = PressableProps &
  VariantProps<typeof inlineIconButtonVariants> & {
    /** Required: icon-only controls must name the action. */
    "aria-label": string
  }

/** Small icon action that sits inside a chip or a line of text. */
export const InlineIconButton = React.forwardRef<
  HTMLButtonElement,
  InlineIconButtonProps
>(function InlineIconButton(
  { className, size, type = "button", ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(inlineIconButtonVariants({ size }), className)}
      {...props}
    />
  )
})

export const textButtonVariants = cva(
  [pressableBase, "inline-flex items-center gap-1 rounded-sm font-medium"].join(
    " "
  ),
  {
    variants: {
      tone: {
        /** Secondary text actions ("Show more", "Resend"). */
        muted: "text-muted-foreground hover:text-foreground",
        /** Link-like actions in the accent color. */
        accent: "text-primary underline-offset-2 hover:underline",
        destructive: "text-destructive-text underline-offset-2 hover:underline",
      },
      size: {
        xs: "text-xs [&_svg]:size-3",
        sm: "text-sm [&_svg]:size-3.5",
        inherit: "",
      },
    },
    defaultVariants: { tone: "muted", size: "xs" },
  }
)

export type TextButtonProps = PressableProps &
  VariantProps<typeof textButtonVariants>

/** A clickable piece of text — no box, no padding. */
export const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  function TextButton(
    { className, tone, size, type = "button", ...props },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(textButtonVariants({ tone, size }), className)}
        {...props}
      />
    )
  }
)

export const tabButtonVariants = cva(
  [
    pressableBase,
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-medium",
  ].join(" "),
  {
    variants: {
      /** `underline`: page tabs on a bottom border; `pill`: tabs in a panel header. */
      variant: {
        underline: "-mb-px h-10 border-b-2 px-2.5 text-sm",
        pill: "h-8 rounded-md px-2.5 text-xs",
      },
      selected: { true: "", false: "" },
    },
    compoundVariants: [
      {
        variant: "underline",
        selected: true,
        className: "border-primary text-foreground",
      },
      {
        variant: "underline",
        selected: false,
        className:
          "border-transparent text-muted-foreground hover:text-foreground",
      },
      {
        variant: "pill",
        selected: true,
        className: "bg-primary text-primary-foreground shadow-sm",
      },
      {
        variant: "pill",
        selected: false,
        className:
          "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
      },
    ],
    defaultVariants: { variant: "underline", selected: false },
  }
)

export type TabButtonProps = PressableProps &
  VariantProps<typeof tabButtonVariants>

/** Tab trigger; sets role="tab" and aria-selected. */
export const TabButton = React.forwardRef<HTMLButtonElement, TabButtonProps>(
  function TabButton(
    { className, variant, selected, type = "button", ...props },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type}
        role="tab"
        aria-selected={Boolean(selected)}
        className={cn(tabButtonVariants({ variant, selected }), className)}
        {...props}
      />
    )
  }
)
